import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
   Button,
   Chip,
   Div,
   Divider,
   Icon,
   Image,
   isApiError,
   Loader,
   PageHeader,
   Text,
   useBooleanState,
   useMediaQuery,
   useTheme,
   type ModalRef,
} from "react-better-html";

import { propertySpaceKindIcon } from "../../../constants/properties";

import { type Property, type PropertySpaceKind, type PropertySpaceSaleType } from "../../../types/property";
import { type Workspace } from "../../../types/workspace";
import { type ApiErrorResponse } from "../../../types/api";

import { useParams, useReduxAction, useReduxState } from "../../../utils/hooks";
import { formatPrice, getPropertyTitle } from "../../../utils/functions";
import { useI18n } from "../../../utils/i18n";
import { dayjs } from "../../../utils/dayjs";

import ImageViewModal from "../../../components/modals/ImageViewModal";
import Section from "../../../components/structure/Section";

type HeaderStatItemProps = {
   name: string;
   value?: string | number;
   empty?: boolean;
};

const HeaderStatItem = memo(function HeaderStatItem({ name, value, empty }: HeaderStatItemProps) {
   const theme = useTheme();

   return (
      <Div.column flex={1} gap={theme.styles.gap / 2}>
         <Text fontSize={14} color={theme.colors.textSecondary}>
            {name}
         </Text>

         <Text fontWeight={700} color={empty ? theme.colors.textSecondary : undefined}>
            {value}
         </Text>
      </Div.column>
   );
});

function SingleProperty() {
   const theme = useTheme();
   const { t } = useI18n();
   const mediaQuery = useMediaQuery();
   const { workspaceId, propertyId } = useParams<{ workspaceId: Workspace["id"]; propertyId: Property["id"] }>();

   const isMobile = mediaQuery.size1200;

   const imageViewModalRef = useRef<ModalRef>(null);

   const propertiesList = useReduxState((state) => state.properties.list);
   const workspaceProperties = useReduxState((state) => state.properties.workspaceProperties);
   const selectedOrganizationId = useReduxState((state) => state.organizations.selectedOrganizationId);

   const getPropertyById = useReduxAction((actions) => actions.properties.getPropertyById);
   const getWorkspacePropertyById = useReduxAction((actions) => actions.properties.getWorkspacePropertyById);

   const [propertyDoesNotExist, setPropertyDoesNotExist] = useBooleanState();
   const [imageViewModalImageSrc, setImageViewModalImageSrc] = useState<string>();

   const thisProperty = useMemo(
      () =>
         (workspaceId ? workspaceProperties?.[workspaceId]?.items : propertiesList?.items)?.find(
            (property) => property.property.id === propertyId,
         ),
      [propertiesList, propertyId, workspaceProperties, workspaceId],
   );

   const onClickMainImage = useCallback(() => {
      setImageViewModalImageSrc(thisProperty?.images?.[0].url);
      imageViewModalRef.current?.open();
   }, [thisProperty]);
   const onClickSecondImage = useCallback(() => {
      setImageViewModalImageSrc(thisProperty?.images?.[1].url);
      imageViewModalRef.current?.open();
   }, [thisProperty]);
   const onClickOtherImages = useCallback(() => {
      setImageViewModalImageSrc(thisProperty?.images?.[2].url);
      imageViewModalRef.current?.open();
   }, [thisProperty]);
   const onCloseImageViewModal = useCallback(() => {
      setTimeout(() => {
         setImageViewModalImageSrc(undefined);
      }, 0.3 * 1000);
   }, []);
   const onClickNextImage = useCallback(() => {
      setImageViewModalImageSrc((oldValue) => {
         const thisImageIndex = thisProperty?.images?.findIndex((image) => image.url === oldValue);
         const nextImage =
            thisImageIndex !== undefined && thisImageIndex !== -1
               ? thisProperty?.images?.[thisImageIndex + 1]
               : undefined;

         return nextImage?.url ?? thisProperty?.images?.[0].url ?? oldValue;
      });
   }, [thisProperty]);
   const onClickPreviousImage = useCallback(() => {
      setImageViewModalImageSrc((oldValue) => {
         const thisImageIndex = thisProperty?.images?.findIndex((image) => image.url === oldValue);
         const nextImage =
            thisImageIndex !== undefined && thisImageIndex !== -1
               ? thisProperty?.images?.[thisImageIndex - 1]
               : undefined;

         return nextImage?.url ?? thisProperty?.images?.at(-1)?.url ?? oldValue;
      });
   }, [thisProperty]);

   const propertyImages = useMemo(() => thisProperty?.images?.slice(1, 3), [thisProperty]);

   const propertySpaces = useMemo(() => {
      if (!thisProperty) return undefined;

      const saleTypeOrder: Record<PropertySpaceSaleType, number> = {
         INCLUDED: 0,
         EXTRA_REQUIRED: 1,
         EXTRA_OPTIONAL: 2,
         UNKNOWN: 3,
      };

      return thisProperty.spaces
         ? [...thisProperty.spaces].sort(
              (a, b) => (saleTypeOrder[a.saleType ?? "UNKNOWN"] ?? 3) - (saleTypeOrder[b.saleType ?? "UNKNOWN"] ?? 3),
           )
         : thisProperty.spaces;
   }, [thisProperty]);

   useEffect(() => {
      if (!propertyId) return;

      if (workspaceId) {
         getWorkspacePropertyById({
            id: propertyId,
            workspaceId,
         });
      } else {
         getPropertyById({
            id: propertyId,
            onError: (error) => {
               if (isApiError(error)) {
                  const response: ApiErrorResponse = error.data;

                  if (response.error === "ENTITY_NOT_FOUND") setPropertyDoesNotExist.setTrue();
               }
            },
         });
      }
   }, [workspaceId, propertyId, selectedOrganizationId]);

   const imageGap = theme.styles.gap / 2;
   const moreImagesCount = Math.max(0, (thisProperty?.images?.length ?? 0) - 3);

   return thisProperty ? (
      <>
         <PageHeader
            title={getPropertyTitle(thisProperty.property)}
            description={`${t((l) => l.pages.singleProperty.trackedSince)} ${dayjs(thisProperty.metadata.addedAt).format("DD MMM, YYYY")}`}
         />

         <Div.row gap={theme.styles.space}>
            <Div.column flex={3} gap={theme.styles.space}>
               <Div.grid gridTemplateColumns="1fr 1fr 1fr" gridTemplateRows="1fr 1fr" height={340} gap={imageGap}>
                  <Image
                     src={thisProperty.images?.[0].url}
                     width="100%"
                     height="100%"
                     objectFit="cover"
                     borderRadius={theme.styles.borderRadius}
                     gridColumn="1 / span 2"
                     gridRow="1 / span 2"
                     onClick={onClickMainImage}
                  />

                  {propertyImages?.map((image, index) => (
                     <Div
                        position="relative"
                        width="100%"
                        height="100%"
                        borderRadius={theme.styles.borderRadius}
                        gridColumn={3}
                        gridRow={index + 1}
                        overflow="hidden"
                        onClick={index === 0 ? onClickSecondImage : onClickOtherImages}
                        key={image.url}
                     >
                        <Image src={image.url} width="100%" height="100%" objectFit="cover" />

                        {index === 1 && moreImagesCount && (
                           <Div.row
                              position="absolute"
                              width="100%"
                              height="100%"
                              top={0}
                              left={0}
                              backgroundColor={theme.colors.label + "80"}
                              justifyContent="center"
                              alignItems="center"
                           >
                              <Text fontSize={20} color={theme.colors.base}>
                                 +{moreImagesCount}
                              </Text>
                           </Div.row>
                        )}
                     </Div>
                  ))}
               </Div.grid>

               <Div.box
                  title={formatPrice(
                     thisProperty.property.lowestActivePrice ?? 0,
                     thisProperty.property.lowestActiveCurrency,
                  )}
                  rightElement={
                     <Text fontSize={14} color={theme.colors.textSecondary}>
                        {thisProperty.property.areaSqm
                           ? `${formatPrice((thisProperty.property.lowestActivePrice ?? 0) / thisProperty.property.areaSqm, thisProperty.property.lowestActiveCurrency)} / m²`
                           : "-"}
                     </Text>
                  }
               >
                  <Div.column gap={theme.styles.space}>
                     <Div.row alignItems="center" justifyContent="space-between">
                        <HeaderStatItem name={t((l) => l.common.rooms)} value={thisProperty.property.rooms ?? "-"} />
                        <HeaderStatItem name={t((l) => l.common.beds)} value={thisProperty.property.bedrooms ?? "-"} />
                        <HeaderStatItem
                           name={t((l) => l.common.area)}
                           value={thisProperty.property.areaSqm ? `${thisProperty.property.areaSqm} m²` : "-"}
                        />
                        <HeaderStatItem
                           name={
                              thisProperty.property.kind === "HOUSE"
                                 ? t((l) => l.common.floors)
                                 : t((l) => l.common.floor)
                           }
                           value={
                              thisProperty.property.kind === "HOUSE"
                                 ? thisProperty.property.floorsTotal
                                 : `${thisProperty.property.floor || "-"} / ${thisProperty.property.floorsTotal || "-"}`
                           }
                        />
                        <HeaderStatItem
                           name={t((l) => l.common.built)}
                           value={thisProperty.property.yearBuilt ?? "-"}
                        />
                     </Div.row>

                     <Divider.horizontal />

                     <Div.row alignItems="center" justifyContent="space-between">
                        {(thisProperty.property.kind === "APARTMENT" || thisProperty.property.kind === "HOUSE") && (
                           <HeaderStatItem
                              name={t((l) => l.common.livingArea)}
                              value={
                                 thisProperty.property.livingAreaSqm ? `${thisProperty.property.livingAreaSqm} m²` : "-"
                              }
                           />
                        )}
                        {(thisProperty.property.kind === "HOUSE" || thisProperty.property.kind === "LAND") && (
                           <HeaderStatItem
                              name={t((l) => l.common.landArea)}
                              value={thisProperty.property.landAreaSqm ? `${thisProperty.property.areaSqm} m²` : "-"}
                           />
                        )}
                        {(thisProperty.property.kind === "APARTMENT" || thisProperty.property.kind === "OFFICE") && (
                           <HeaderStatItem
                              name={t((l) => l.common.commonArea)}
                              value={
                                 thisProperty.property.commonPartsSqm
                                    ? `${thisProperty.property.commonPartsSqm} m²`
                                    : "-"
                              }
                           />
                        )}
                        {(thisProperty.property.kind === "APARTMENT" || thisProperty.property.kind === "OFFICE") && (
                           <HeaderStatItem
                              name={t((l) => l.common.maintenanceFee)}
                              value={`${
                                 thisProperty.property.maintenanceFee
                                    ? formatPrice(
                                         thisProperty.property.maintenanceFee,
                                         thisProperty.property.maintenanceCurrency ??
                                            thisProperty.property.lowestActiveCurrency,
                                      )
                                    : "-"
                              } / ${t((l) => l.enums.time.month)}`}
                           />
                        )}
                     </Div.row>
                  </Div.column>
               </Div.box>

               {propertySpaces ? (
                  propertySpaces.length > 0 ? (
                     <Section title={t((l) => l.pages.singleProperty.spaces)}>
                        <Div.grid
                           gridTemplateColumns={`repeat(${mediaQuery.size700 ? 2 : mediaQuery.size1300 ? 3 : 4}, 1fr)`}
                           gap={theme.styles.gap}
                        >
                           {propertySpaces.map((space, index) => {
                              const spaceKind: PropertySpaceKind = space.kind ?? "OTHER";

                              const isToiletInApartment =
                                 (spaceKind === "BATHROOM" ||
                                    spaceKind === "BATHROOM_WITH_TOILET" ||
                                    spaceKind === "TOILET") &&
                                 (thisProperty.property.kind === "APARTMENT" ||
                                    thisProperty.property.kind === "OFFICE") &&
                                 space.placement === "INTERNAL";

                              return (
                                 <Div.box backgroundColor={theme.colors.backgroundBase} key={index}>
                                    <Div.column height="100%">
                                       <Div.row
                                          alignItems="center"
                                          justifyContent="space-between"
                                          gap={theme.styles.gap}
                                          marginBottom={theme.styles.space}
                                       >
                                          <Div.box width={52} height={52} padding={0}>
                                             <Div.row
                                                width="100%"
                                                height="100%"
                                                alignItems="center"
                                                justifyContent="center"
                                             >
                                                <Icon name={propertySpaceKindIcon[spaceKind]} size={24} />
                                             </Div.row>
                                          </Div.box>

                                          {space.areaSqm && <Text fontSize={14}>{space.areaSqm} m²</Text>}
                                       </Div.row>

                                       <Text fontSize={18} fontWeight={700}>
                                          {(space.count ?? 1) > 1 ? `${space.count} ` : ""}
                                          {t((l) => l.enums.propertySpaceKind[spaceKind])}
                                       </Text>
                                       {space.placement && space.placement !== "UNKNOWN" && !isToiletInApartment && (
                                          <Text color={theme.colors.textSecondary}>
                                             {t((l) => l.enums.propertySpacePlacement[space.placement ?? "UNKNOWN"])}
                                          </Text>
                                       )}

                                       {space.notes && (
                                          <Text color={theme.colors.textSecondary} marginTop={theme.styles.gap}>
                                             {space.notes}
                                          </Text>
                                       )}

                                       <Div marginTop="auto" paddingTop={theme.styles.gap}>
                                          <Chip.colored
                                             fontSize={14}
                                             fontWeight={700}
                                             text={t((l) => l.enums.propertySpaceSaleType[space.saleType ?? "UNKNOWN"])}
                                             color={
                                                space.saleType === "INCLUDED"
                                                   ? theme.colors.success
                                                   : space.saleType === "EXTRA_REQUIRED"
                                                     ? theme.colors.warn
                                                     : space.saleType === "EXTRA_OPTIONAL"
                                                       ? theme.colors.info
                                                       : undefined
                                             }
                                          />
                                       </Div>
                                    </Div.column>
                                 </Div.box>
                              );
                           })}
                        </Div.grid>
                     </Section>
                  ) : undefined
               ) : (
                  <Loader.box />
               )}
            </Div.column>

            {!isMobile && (
               <Div flex={1}>
                  <Div.box>
                     <Text fontSize={14} color={theme.colors.textSecondary}>
                        {t((l) => l.common.notes)}
                     </Text>

                     <Text>-</Text>
                  </Div.box>
               </Div>
            )}
         </Div.row>

         <ImageViewModal
            imageSrc={imageViewModalImageSrc}
            onClickNext={onClickNextImage}
            onClickPrevious={onClickPreviousImage}
            onClose={onCloseImageViewModal}
            ref={imageViewModalRef}
         />
      </>
   ) : propertyDoesNotExist ? (
      <Div.column alignItems="center" justifyContent="center" gap={theme.styles.space}>
         <Text textAlign="center">{t((l) => l.pages.singleProperty.emptyStateMessage)}</Text>

         <Button text={t((l) => l.common.back)} href="/properties" />
      </Div.column>
   ) : (
      <Loader.box />
   );
}

export default memo(SingleProperty);
